
import { Form, Formik } from "formik"
import { useEffect, useState } from "react";
import * as Yup from 'yup';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import FileUploader from "../utils/imageUploader";
import { notificationService } from "@/services/notification.service";
import { IAdminEvent, IAdminEventDto } from "@/interfaces/entities/admin-event";
import TimeRangePickerFormik from "../utils/timePicker";
import useCreateAdminEvent from "@/hooks/adminEvent/useCreateEvent";
import useUpdateAdminEvent from "@/hooks/adminEvent/useUpdateEvent";
import { Input } from "antd";
import { Button } from "antd";
import Loader from "../Loader";

dayjs.extend(customParseFormat);

type PropType = { editEvent: IAdminEvent | null; setToggle: () => void }


export const CreateAdminEvent = ({ editEvent, setToggle }: PropType) => {

    const [loading, setLoading] = useState(false);
    const createEvent = useCreateAdminEvent()
    const updateEvent = useUpdateAdminEvent()

    const [initialValues, setInitialValues] = useState<IAdminEventDto>({
        title: '',
        description: '',
        location: '',
        date: "",
        time: "",
        img: null,
    });

    const handleSubmit = async (values: IAdminEventDto, { resetForm }: { resetForm: () => void }) => {

        setLoading(true);
        if (!editEvent) {
            const formData = new FormData();
            formData.append("title", values.title);
            formData.append("location", values.location);
            formData.append("description", values.description);
            formData.append("date", values.date);
            formData.append("time", values.time);

            // Handle image file
            if (values.img && values.img instanceof File) {
                formData.append("img", values.img); // actual file
            } else if (typeof values.img === "string") {
                formData.append("img_url", values.img); // just the URL
            }

            try {
                await createEvent.mutateAsync(formData);
                resetForm();
                setToggle()
            } catch (error) {
                notificationService.error("Event Created failed");
            }
            finally{
                setLoading(false);
            }
        }
        else {
            const formData = new FormData();
            formData.append("title", values.title);
            formData.append("location", values.location);
            formData.append("description", values.description);
            formData.append("date", values.date);
            formData.append("time", values.time);

            // Handle image file
            if (values.img && values.img instanceof File) {
                formData.append("img", values.img); // actual file
            } else if (typeof values.img === "string") {
                formData.append("img_url", values.img); // just the URL
            }

            try {
                await updateEvent.mutateAsync([editEvent.id, formData]);
                resetForm();
                setToggle()
            } catch (error) {
                notificationService.error("Event Updated failed");
            }
            finally{
                setLoading(false);
            }
        }
    };

    const handleCancel = () => {
        setToggle()
        setInitialValues({
            title: '',
            location: '',
            description: '',
            date: "",
            time: "",
            img: null,
        })
    }

    const eventValidationSchema = Yup.object().shape({
        title: Yup.string().required('Title is required'),
        location: Yup.string().required('Location is required'),
        date: Yup.string().required('Date is required'),
        description: Yup.string().required('Description is required'),
        time: Yup.string().required('Time is required'),
        img: Yup.mixed().nullable().required('Image is required'),
    });

    useEffect(() => {
        if (editEvent) {
            setInitialValues({
                title: editEvent.title,
                location: editEvent.location,
                description: editEvent.description,
                date: editEvent.date,
                time: editEvent.time,
                img: editEvent?.img || null,
            })
        }

    }, [editEvent])


    return (
        <div className="w-[100%] h-[100%]">
{loading && (
				<div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/30 flex-col gap-5">
					<Loader />
					<p className="text-[20px] font-semibold">Processing...</p>
				</div>
			)}
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validationSchema={eventValidationSchema}
                enableReinitialize={true}
            >
                {({ handleChange, setFieldValue, handleBlur, values, errors, touched }) => (
                    <Form >
                        <div className="flex justify-between gap-5 mb-5 w-full">
                            {/* Title Input */}
                            <div className="w-4/5 flex flex-col gap-3">
                                <label className="text-[20px] font-semibold">Title</label>
                                <Input
                                    name="title"
                                    value={values.title}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="Enter title"
                                    className="py-[10px] h-[50px] text-[18px]"
                                />
                                {touched.title && errors.title && (
                                    <div className="text-red-500">{errors.title}</div>
                                )}
                            </div>
                            <div className="w-1/5 flex flex-col gap-3">
                                <label className="text-[20px] font-semibold">Date</label>
                                <DatePicker
                                    name="date"
                                    format="MMM DD YYYY" // display format
                                    value={values.date ? dayjs(values.date, 'YYYY-MM-DD') : null}
                                    onChange={(date) => {
                                        if (date && dayjs(date).isValid()) {
                                            // Store in YYYY-MM-DD for backend & consistency
                                            setFieldValue('date', date.format('YYYY-MM-DD'));
                                        } else {
                                            setFieldValue('date', '');
                                        }
                                    }}
                                    style={{ width: '100%' }}
                                    className="py-[10px] h-[50px] text-[16px]"
                                    allowClear
                                />

                                {touched.date && errors.date && (
                                    <div className="text-red-500">{errors.date}</div>
                                )}
                            </div>
                        </div>
                        <div className="flex justify-between gap-5 mb-5 w-full">
                            <div className="w-4/5 flex flex-col gap-3">
                                <label className="text-[20px] font-semibold">Location</label>
                                <Input
                                    name="location"
                                    value={values.location}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="Enter Location"
                                    className="py-[10px] h-[50px] text-[18px]"
                                />
                                {touched.location && errors.location && (
                                    <div className="text-red-500">{errors.location}</div>
                                )}
                            </div>
                            <div className="w-1/5 flex flex-col gap-3">
                                <label className="text-[20px] font-semibold">Time</label>
                                <TimeRangePickerFormik
                                    value={values.time}
                                    onChange={(val) => setFieldValue("time", val)}
                                />
                                {touched.time && errors.time && (
                                    <div className="text-red-500">{errors.time}</div>
                                )}
                            </div>
                        </div>
                        {/* Description Input */}
                        <div className="flex flex-col gap-3">
                            <label className="text-[20px] font-semibold">Description</label>
                            <Input.TextArea
                                name="description"
                                value={values.description}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Enter description"
                                rows={4}
                                className="py-[10px] h-[50px] text-[18px]"

                            />
                            {touched.description && errors.description && (
                                <div className="text-red-500">{errors.description}</div>
                            )}
                        </div>

                        {/* Image Upload */}
                        <div className="flex flex-col gap-3 mt-5">
                            <label className="text-[20px] font-semibold">Image</label>
                            <FileUploader mode="image-only" imageField="img" />
                        </div>
                        {touched.img && errors.img && (
                            <div className="text-red-500">{errors.img}</div>
                        )}

                        {/* Submit Button */}
                        <div className="mt-5 w-full flex justify-end pb-10 gap-5">
                            <Button className="text-[16px] font-semibold" htmlType="button" onClick={handleCancel}>
                                Cancel
                            </Button>
                            <Button color="default" variant="solid" className="text-[16px] font-semibold" htmlType="submit">
                                Submit
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    )

}